/*
 * CloudDream header behaviour: mega panels, mobile drawer.
 *
 * The design opens panels on hover. Hover alone is unusable by keyboard and
 * unavailable on touch, so the triggers are real buttons with aria-expanded:
 * hover opens them for a mouse, click and Enter/Space open them for everyone
 * else, Escape closes, and focus leaving the header closes.
 *
 * Wrapped in an IIFE because a second instance of this section on one
 * template loads this file with its own <script src>, so the browser
 * executes it twice; without the wrapper the second execution throws on the
 * repeated top-level `class` declaration before the customElements.get()
 * guard below ever runs.
 */
(function () {
class CdHeader extends HTMLElement {
  connectedCallback() {
    this.openOnHover = this.dataset.openOn !== 'click';
    this.triggers = Array.from(this.querySelectorAll('[data-panel-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.drawer = this.querySelector('[data-drawer]');
    this.openPanel = null;

    this.bindPanels();
    this.bindDrawer();

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' && this.drawer && !this.drawer.hidden) {
        this.trapDrawerTab(event);
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.drawer && !this.drawer.hidden) {
        this.closeDrawer();
      } else if (this.openPanel) {
        const trigger = this.triggerFor(this.openPanel);
        this.close();
        if (trigger) trigger.focus();
      }
    });

    // A click anywhere else on the page dismisses an open panel.
    this.onDocumentClick = (event) => {
      if (this.openPanel && !this.contains(event.target)) this.close();
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.unlockScroll();
  }

  /* ------------------------------------------------------------- panels */

  bindPanels() {
    this.triggers.forEach((trigger) => {
      const panel = document.getElementById(trigger.dataset.panelTrigger);
      if (!panel) return;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.openPanel === panel) {
          this.close();
        } else {
          this.open(panel);
        }
      });

      if (this.openOnHover) {
        trigger.addEventListener('mouseenter', () => this.open(panel));
      }
    });

    // Nav items without a panel close whatever is open when hovered, so
    // sliding along the nav does not leave a stale panel behind.
    this.querySelectorAll('.cd-header__navitem:not([data-panel-trigger])').forEach((item) => {
      item.addEventListener('mouseenter', () => this.close());
    });

    const bar = this.querySelector('.cd-header__bar');
    if (bar && this.openOnHover) {
      bar.querySelector('.cd-header__wordmark')?.addEventListener('mouseenter', () => this.close());
      bar.querySelector('.cd-header__icons')?.addEventListener('mouseenter', () => this.close());
    }

    if (this.openOnHover) {
      this.addEventListener('mouseleave', () => this.close());
    }

    this.addEventListener('focusout', () => {
      // Defer so document.activeElement has settled on the new target.
      requestAnimationFrame(() => {
        if (this.openPanel && !this.contains(document.activeElement)) this.close();
      });
    });
  }

  triggerFor(panel) {
    return this.triggers.find((t) => t.dataset.panelTrigger === panel.id) || null;
  }

  open(panel) {
    if (this.openPanel === panel) return;
    this.close();
    panel.hidden = false;
    const trigger = this.triggerFor(panel);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.openPanel = panel;
  }

  close() {
    if (!this.openPanel) return;
    const trigger = this.triggerFor(this.openPanel);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.openPanel.hidden = true;
    this.openPanel = null;
    this.classList.remove('is-open');
  }

  /* ------------------------------------------------------------- drawer */

  bindDrawer() {
    if (!this.drawer) return;
    this.drawerToggle = this.querySelector('[data-drawer-toggle]');

    this.drawerToggle?.addEventListener('click', () => {
      if (this.drawer.hidden) {
        this.openDrawer();
      } else {
        this.closeDrawer();
      }
    });

    this.querySelectorAll('[data-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });
  }

  openDrawer() {
    this.drawer.hidden = false;
    this.drawerToggle?.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.drawer.querySelector('.cd-drawer__close')?.focus();
  }

  closeDrawer() {
    if (this.drawer.hidden) return;
    this.drawer.hidden = true;
    this.drawerToggle?.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    this.drawerToggle?.focus();
  }

  /* The drawer is modal, so Tab cycles inside it instead of walking into
     the page that sits scroll-locked behind it. */
  trapDrawerTab(event) {
    const focusable = Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    this.scrollLocked = true;
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;
    document.body.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('cd-header')) {
  customElements.define('cd-header', CdHeader);
}
})();
